// components/Admin/EmoticonManager/EmoticonLogTable.jsx

import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Stack, TextField, Typography, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination
} from '@mui/material';
import api from '../../../api/axios';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';   // ✅ 추가

const EmoticonLogTable = () => {
    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [page, setPage] = useState(1);

    const rowsPerPage = 10;

    useEffect(() => {
        api.get('/api/management/emoticon-logs/')
            .then(res => setLogs(res.data || []))
            .catch(err => console.error('로그 불러오기 실패', err));
    }, []);

    // 🔍 필터링된 데이터
    const filteredLogs = useMemo(() => (
        logs.filter(log => {
            const keyword = search.toLowerCase();
            const matchesKeyword =
                log.user?.toLowerCase().includes(keyword) ||
                log.emoticon?.toLowerCase().includes(keyword);

            const matchesDate = selectedDate
                ? log.purchased_at.startsWith(selectedDate)
                : true;

            return matchesKeyword && matchesDate;
        })
    ), [logs, search, selectedDate]);

    const totalPages = Math.ceil(filteredLogs.length / rowsPerPage);
    const paginated = filteredLogs.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    //📥 엑셀 내보내기 
    const handleExportExcel = () => {
        const rows = filteredLogs.map((log) => ({
            ID: log.id,
            구매자: log.user,
            이모티콘: log.emoticon,
            가격: log.point_spent,
            구매일시: dayjs(log.purchased_at).format('YYYY-MM-DD HH:mm'),
        }));

        if (!rows.length) {
            alert('엑셀로 내보낼 로그가 없습니다.');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'EmoticonLogs');

        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD 
        XLSX.writeFile(workbook, `emoticon_logs_${today}.xlsx`);
    };



    return (
        <Box
            sx={{
                '@media (max-width:600px)': {
                    transform: 'scale(0.95)',
                    transformOrigin: 'top left',
                },
            }}
        >
            {/* 🔎 필터 바 */}
            <Stack
                direction="row"
                spacing={2}
                sx={{ mb: 2 }}
                flexWrap="wrap"
                alignItems="center"
            >
                <TextField
                    size="small"
                    label="검색 (구매자 / 이모티콘명)"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />

                <TextField
                    type="date"
                    size="small"
                    label="날짜"
                    InputLabelProps={{ shrink: true }}
                    value={selectedDate}
                    onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
                />

                <Typography variant="body2" sx={{ ml: 'auto', pr: 1 }}>
                    총 {filteredLogs.length}건
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportExcel}
                >
                    📥 엑셀로 내보내기
                </Button>
            </Stack>

            {/* 📄 테이블 */}
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 2,
                    overflowX: 'auto',
                    minWidth: '100%',
                    '@media (max-width:600px)': {
                        minWidth: 'max-content',
                    },
                }}
            >
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell>구매자</TableCell>
                            <TableCell>이모티콘</TableCell>
                            <TableCell>가격</TableCell>
                            <TableCell>구매일시</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                    🔍 조건에 맞는 로그가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell>{log.user}</TableCell>
                                    <TableCell>{log.emoticon}</TableCell>
                                    <TableCell>{log.point_spent.toLocaleString()} Leaf🌿</TableCell>
                                    <TableCell>
                                        {dayjs(log.purchased_at).format('YYYY-MM-DD HH:mm')}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 📄 페이지네이션 */}
            {totalPages > 1 && (
                <Box mt={2} display="flex" justifyContent="center">
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        color="primary"
                        showFirstButton
                        showLastButton
                        sx={{
                            '@media (max-width:600px)': { transform: 'scale(0.9)' },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default EmoticonLogTable;
